import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../actions/api";
import { hideLoader, showLoader } from "./loaderSlice";

const emptyListPayload = {
  clinicId: null,
  totalChats: 0,
  unreadChats: 0,
  totalUnreadMessages: 0,
  chats: [],
};

export const getWebChatList = createAsyncThunk(
  "webChat/getWebChatList",
  async ({ filter = "all", search = "", showLoading = false } = {}, thunkAPI) => {
    try {
      if (showLoading) thunkAPI.dispatch(showLoader());
      const params = { filter: filter || "all" };
      if (search && String(search).trim()) {
        params.search = String(search).trim();
      }
      const res = await api({ contentType: true, auth: true }).get(`web-chat/list`, {
        params,
      });
      if (showLoading) thunkAPI.dispatch(hideLoader());
      const body = res?.data;
      // Body may use 404 when empty; HTTP is still 200
      if (body?.data) {
        return body.data;
      }
      return emptyListPayload;
    } catch (err) {
      if (showLoading) thunkAPI.dispatch(hideLoader());
      const data = err?.response?.data?.data;
      if (data) return data;
      return emptyListPayload;
    }
  }
);

export const getWebChatById = createAsyncThunk(
  "webChat/getWebChatById",
  async (chatId, thunkAPI) => {
    try {
      const res = await api({ contentType: true, auth: true }).get(
        `web-chat/${chatId}`
      );
      return res?.data?.data ?? null;
    } catch (err) {
      return null;
    }
  }
);

export const getWebChatMessages = createAsyncThunk(
  "webChat/getWebChatMessages",
  async ({ chatId, page, itemSize, showLoading = false }, thunkAPI) => {
    try {
      if (showLoading) thunkAPI.dispatch(showLoader());
      const params = {};
      if (page != null && itemSize != null) {
        params.page = page;
        params.itemSize = itemSize;
      }
      const res = await api({ contentType: true, auth: true }).get(
        `web-chat/${chatId}/messages`,
        { params }
      );
      if (showLoading) thunkAPI.dispatch(hideLoader());
      return {
        chatId,
        messages: res?.data?.data ?? [],
        totalRecords: res?.data?.totalRecords ?? 0,
      };
    } catch (err) {
      if (showLoading) thunkAPI.dispatch(hideLoader());
      return { chatId, messages: [], totalRecords: 0 };
    }
  }
);

export const sendWebChatMessage = createAsyncThunk(
  "webChat/sendWebChatMessage",
  async ({ chatId, content, messageType = "TEXT", files = [] }, thunkAPI) => {
    try {
      const form = new FormData();
      if (content != null && String(content).trim() !== "") {
        form.append("content", content);
      }
      if (messageType) {
        form.append("messageType", messageType);
      }
      (files || []).forEach((file) => {
        if (file) form.append("files", file);
      });
      const res = await api({
        contentType: true,
        auth: true,
        formData: true,
      }).post(`web-chat/${chatId}/messages`, form);
      return res?.data?.data ?? null;
    } catch (err) {
      return null;
    }
  }
);

export const markWebChatRead = createAsyncThunk(
  "webChat/markWebChatRead",
  async (chatId, thunkAPI) => {
    try {
      const res = await api({ contentType: true, auth: true }).put(
        `web-chat/${chatId}/read`
      );
      return res?.data?.data ?? null;
    } catch (err) {
      return null;
    }
  }
);

export const toggleWebChatFavorite = createAsyncThunk(
  "webChat/toggleWebChatFavorite",
  async ({ chatId, favorite }, thunkAPI) => {
    try {
      const params = {};
      if (favorite != null) params.favorite = favorite;
      const res = await api({ contentType: true, auth: true }).put(
        `web-chat/${chatId}/favorite`,
        null,
        { params }
      );
      return res?.data?.data ?? null;
    } catch (err) {
      return null;
    }
  }
);

export const getWhatsAppInbox = createAsyncThunk(
  "webChat/getWhatsAppInbox",
  async (_, thunkAPI) => {
    try {
      const res = await api({ contentType: true, auth: true }).get(
        `web-chat/whatsapp/messages`
      );
      const data = res?.data?.data;
      return Array.isArray(data) ? data : [];
    } catch (err) {
      return [];
    }
  }
);

export const sendWhatsAppMessage = createAsyncThunk(
  "webChat/sendWhatsAppMessage",
  async ({ to, body }, thunkAPI) => {
    try {
      const res = await api({ contentType: true, auth: true }).post(
        `web-chat/whatsapp/send`,
        { to, body }
      );
      return res?.data?.data ?? null;
    } catch (err) {
      const message =
        err?.response?.data?.message || "WhatsApp message was not sent";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const startWebChat = createAsyncThunk(
  "webChat/startWebChat",
  async (
    { clientUserId, petId, whatsappSynced, source = "WEB", showLoading = false },
    thunkAPI
  ) => {
    try {
      if (showLoading) thunkAPI.dispatch(showLoader());
      const body = { clientUserId, source };
      if (petId != null) body.petId = petId;
      if (whatsappSynced != null) body.whatsappSynced = whatsappSynced;
      const res = await api({ contentType: true, auth: true }).post(
        `web-chat/start`,
        body
      );
      if (showLoading) thunkAPI.dispatch(hideLoader());
      return res?.data?.data ?? null;
    } catch (err) {
      if (showLoading) thunkAPI.dispatch(hideLoader());
      return null;
    }
  }
);

export const webChatSlice = createSlice({
  name: "webChat",
  initialState: {
    listMeta: emptyListPayload,
    list: [],
    messagesByChatId: {},
    whatsappInbox: [],
    selectedChatId: null,
  },
  reducers: {
    setSelectedWebChatId: (state, action) => {
      state.selectedChatId = action.payload ?? null;
    },
    appendWebChatMessage: (state, action) => {
      const msg = action.payload;
      if (!msg?.chatId) return;
      const key = String(msg.chatId);
      const existing = state.messagesByChatId[key] || [];
      if (existing.some((m) => m.messageId === msg.messageId)) return;
      state.messagesByChatId[key] = [...existing, msg];
    },
    upsertWebChatInList: (state, action) => {
      const item = action.payload;
      if (!item?.chatId) return;
      const idx = state.list.findIndex(
        (c) => String(c.chatId) === String(item.chatId)
      );
      if (idx >= 0) {
        state.list[idx] = { ...state.list[idx], ...item };
        const [updated] = state.list.splice(idx, 1);
        state.list.unshift(updated);
      } else {
        state.list.unshift(item);
      }
    },
    removeWebChatFromList: (state, action) => {
      const chatId = action.payload;
      if (chatId == null) return;
      state.list = (state.list || []).filter(
        (c) => String(c.chatId) !== String(chatId)
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getWebChatList.fulfilled, (state, action) => {
      const data = action.payload || emptyListPayload;
      state.listMeta = {
        clinicId: data.clinicId ?? null,
        totalChats: data.totalChats ?? 0,
        unreadChats: data.unreadChats ?? 0,
        totalUnreadMessages: data.totalUnreadMessages ?? 0,
        chats: data.chats || [],
      };
      const apiChats = data.chats || [];
      const apiIds = new Set(apiChats.map((c) => String(c.chatId)));
      const selectedId = state.selectedChatId;
      // Keep drafts / just-opened chats until the API returns them
      const keepLocal = (state.list || []).filter((c) => {
        const id = String(c?.chatId ?? "");
        if (!id || apiIds.has(id)) return false;
        return (
          c?._local ||
          id.startsWith("wa-") ||
          (selectedId != null && id === String(selectedId))
        );
      });
      state.list = [...keepLocal, ...apiChats];
    });
    builder.addCase(getWebChatMessages.fulfilled, (state, action) => {
      const { chatId, messages } = action.payload || {};
      if (chatId == null) return;
      state.messagesByChatId[String(chatId)] = messages || [];
    });
    builder.addCase(sendWebChatMessage.fulfilled, (state, action) => {
      const msg = action.payload;
      if (!msg?.chatId) return;
      const key = String(msg.chatId);
      const existing = state.messagesByChatId[key] || [];
      if (!existing.some((m) => m.messageId === msg.messageId)) {
        state.messagesByChatId[key] = [...existing, msg];
      }
    });
    builder.addCase(markWebChatRead.fulfilled, (state, action) => {
      const item = action.payload;
      if (!item?.chatId) return;
      const idx = state.list.findIndex((c) => c.chatId === item.chatId);
      if (idx >= 0) {
        state.list[idx] = { ...state.list[idx], ...item, unreadCount: 0 };
      }
    });
    builder.addCase(toggleWebChatFavorite.fulfilled, (state, action) => {
      const item = action.payload;
      if (!item?.chatId) return;
      const idx = state.list.findIndex((c) => c.chatId === item.chatId);
      if (idx >= 0) {
        state.list[idx] = { ...state.list[idx], ...item };
      }
    });
    builder.addCase(getWhatsAppInbox.fulfilled, (state, action) => {
      state.whatsappInbox = action.payload || [];
    });
    builder.addCase(sendWhatsAppMessage.fulfilled, (state, action) => {
      const msg = action.payload;
      if (!msg?.id) return;
      const exists = state.whatsappInbox.some((item) => item.id === msg.id);
      if (!exists) {
        state.whatsappInbox = [...state.whatsappInbox, msg];
      }
    });
    builder.addCase(startWebChat.fulfilled, (state, action) => {
      const item = action.payload;
      if (!item?.chatId) return;
      const idx = state.list.findIndex(
        (c) => String(c.chatId) === String(item.chatId)
      );
      if (idx >= 0) {
        state.list[idx] = { ...state.list[idx], ...item, _local: false };
      } else {
        state.list.unshift({ ...item, _local: false });
      }
      // Do not change selectedChatId here — caller decides (avoids opening wrong contact)
    });
  },
});

export const {
  setSelectedWebChatId,
  appendWebChatMessage,
  upsertWebChatInList,
  removeWebChatFromList,
} = webChatSlice.actions;

export default webChatSlice.reducer;
